/**
 * Which region a school's country belongs in, and which region this dashboard
 * is signed in to.
 *
 * Internal admins land in EU by default, and Australian schools kept being
 * created there: 20 existed only in EU on 2026-10-19. dashboard-api refuses
 * them (400 E_WRONG_REGION), but the Create tab makes the school group before
 * the school, so a refusal at that point would still leave the group behind
 * in the wrong region. The forms check here first and block the submit.
 *
 * Mirrors dashboard-api src/utils/region-country.js: countries are slugs
 * ("australia") or labels ("South Africa"); the placeholders "country" and
 * "string" mean unknown, and an unknown country is never blocked.
 */

import api from "@/api";

const REGION_COUNTRIES: Record<string, string[]> = {
  au: ["australia"]
};
const DEFAULT_REGION = "eu";
const PLACEHOLDERS = new Set(["", "country", "string", "null", "undefined"]);

/**
 * The region code for a region name. Deployments report their Cloud Run
 * location ("australia-southeast1", "europe-west1") as DEPLOYMENT_REGION, while
 * countries map to codes ("au", "eu"); comparing the two unnormalised made
 * every country look like it belonged elsewhere (NLM-Test #217). Mirrors
 * normaliseRegion in dashboard-api's region-country.js.
 */
export function normaliseRegion(region: unknown): string {
  const value = String(region ?? "").trim().toLowerCase();
  if (value === "europe-west1") return "eu";
  if (value === "australia-southeast1") return "au";
  return value;
}

export function normaliseCountry(country: unknown): string {
  return String(country ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

/** The region a country belongs in, or null when it is unknown. */
export function regionForCountry(country: unknown): string | null {
  const slug = normaliseCountry(country);
  if (PLACEHOLDERS.has(slug)) return null;
  for (const [region, countries] of Object.entries(REGION_COUNTRIES)) {
    if (countries.includes(slug)) return region;
  }
  return DEFAULT_REGION;
}

/** The region a school with this country belongs in, when that is not `here`. */
export function wrongRegionFor(country: unknown, here: string | null): string | null {
  const region = normaliseRegion(here);
  if (!region) return null;
  const belongs = regionForCountry(country);
  return belongs && belongs !== region ? belongs : null;
}

let regionPromise: Promise<string | null> | null = null;

/**
 * The region of the dashboard-api this session talks to, from its health
 * endpoint. Null when it cannot be told, in which case nothing is blocked
 * here and the server's own check still applies.
 */
export function deploymentRegion(): Promise<string | null> {
  if (!regionPromise) {
    regionPromise = api
      .get("health")
      .then(response => {
        const region = normaliseRegion(response?.data?.region);
        return region && region !== "unknown" ? region : null;
      })
      .catch(() => {
        regionPromise = null;
        return null;
      });
  }
  return regionPromise;
}

/** Test seam. */
export function resetDeploymentRegionForTests(): void {
  regionPromise = null;
}
